import { z } from "zod";
import { NativeCalendarDateSchema } from "./native-calendar.js";
/** The Mini App of the practicum and the webinars: its real screens are previewed in the webinar card. */
export const MINI_APP_ORIGIN = "https://practicum.mc-pashagans.com";
const TELEGRAM_HOSTS = new Set(["t.me", "telegram.me", "telegram.dog"]);
/** https://t.me/<path+query> for a Telegram link (t.me, telegram.me, telegram.dog, with or without www.), null otherwise.
 * The Mini App opens only t.me inside Telegram and keeps the same rule (lib/telegram-link.ts there). */
export function telegramLink(value) {
    let url;
    try {
        url = new URL(value.trim());
    }
    catch {
        return null;
    }
    if ((url.protocol !== "https:" && url.protocol !== "http:") || url.username || url.password || url.port)
        return null;
    if (!TELEGRAM_HOSTS.has(url.hostname.toLowerCase().replace(/^www\./u, "")))
        return null;
    return `https://t.me${url.pathname}${url.search}`;
}
const HttpsLink = z.string().url("Нужна обычная ссылка https.").max(2000).refine(value => {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password;
}, "Нужна обычная ссылка https.");
// Saved as https://t.me/…: a telegram.me link pasted 19.10.2026 (webinars 20–24.10) opened the browser and closed the Mini App.
const SecureLink = HttpsLink.transform(value => telegramLink(value) ?? value).nullable();
// The channel and the care chat are Telegram chats: the Mini App refuses the whole webinar config otherwise.
const TelegramLink = HttpsLink.transform((value, context) => {
    const link = telegramLink(value);
    if (!link)
        context.addIssue({ code: "custom", message: "Нужна ссылка Telegram: https://t.me/…" });
    return link ?? z.NEVER;
});
const CareLink = TelegramLink.refine(value => /^\/m\/[A-Za-z0-9_-]+$/u.test(new URL(value).pathname), "Нужна ссылка на сообщение отдела заботы: https://t.me/m/…").nullable();
export const AcquisitionSettingsSchema = z.object({
    enabled: z.boolean(), registrationChannelUrl: TelegramLink.nullable(), bonusUrl: SecureLink,
    careLinks: z.object({ application: CareLink, bonus: CareLink, follow_up: CareLink }).strict(),
    consultationMode: z.enum(["care", "self_booking"]), qualificationText: z.string().max(4000),
    consentText: z.string().max(2000), version: z.number().int().nonnegative(),
}).strict();
/** Settings a new webinar starts with (Ден 19.10.2026: «завёл эфир, и всё работает»): open, its links and QR work at once;
 * it closes only by «Закрыть приём». Written with the event (NativeCalendarService.writeEvent). An event without a row
 * (created before 20.10.2026) keeps reading as closed. */
export const NEW_EVENT_ACQUISITION_SETTINGS = {
    enabled: true, registrationChannelUrl: null, bonusUrl: null,
    careLinks: { application: null, bonus: null, follow_up: null }, consultationMode: "self_booking",
    qualificationText: "", consentText: "",
};
/** What a webinar without its settings row reads as: closed, as before 20.10.2026. */
export const DEFAULT_ACQUISITION_SETTINGS = { ...NEW_EVENT_ACQUISITION_SETTINGS, enabled: false, version: 0 };
/** What a webinar lacks so that something would break (Ден 20.10.2026: «Не хватает» — only that): a «Материалы» word
 * whose screen has no materials link, or a day without its time (the bot's «через час» and «начинаем» need it). The
 * channel is optional (a registration may go through GetCourse) and a webinar may have no code words. The card and the
 * list say the same. */
export const ACQUISITION_GAPS = ["materials", "time"];
export const ACQUISITION_GAP_LABELS = { materials: "ссылка материалов", time: "время эфира" };
export function acquisitionGaps(settings, event) {
    return ACQUISITION_GAPS.filter(gap => gap === "materials" ? event.phrases.some(phrase => phrase.purpose === "bonus") && !settings.bonusUrl?.trim() : !event.time?.trim());
}
/** Readiness of the webinars of a period for the «Эфиры» list: intake open and what is missing, per event. */
export const AcquisitionReadinessListSchema = z.object({ events: z.array(z.object({
        eventId: z.string().uuid(), enabled: z.boolean(), gaps: z.array(z.enum(ACQUISITION_GAPS)),
    }).strict()) }).strict();
/** GET /api/acquisition/defaults: what a new webinar's form starts with — the latest webinar's materials link. */
export const AcquisitionDefaultsSchema = z.object({ bonusUrl: z.string().nullable(),
    from: z.object({ eventId: z.string().uuid(), title: z.string(), date: NativeCalendarDateSchema }).strict().nullable() }).strict();
export const ACQUISITION_PHRASE_PURPOSES = ["application", "bonus", "offer", "follow_up"];
/** Code words compare without case, ё/е, spacing or compatibility forms; the Mini App and analytics use the same form. */
export function normalizeAcquisitionPhrase(text) {
    return text.normalize("NFKC").trim().toLowerCase().replaceAll("ё", "е").replace(/\s+/gu, " ");
}
/** FNV-1a 32-bit over the UTF-8 bytes of the normalized phrase: 7 characters of [0-9a-z], stable in the browser and in Node. */
export function acquisitionPhraseKey(text) {
    let hash = 0x811c9dc5;
    for (const byte of new TextEncoder().encode(normalizeAcquisitionPhrase(text))) {
        hash ^= byte;
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36).padStart(7, "0");
}
/** Mini App screen a code word opens: a — consultation, b — event materials, f — after the event. */
export function acquisitionPhraseScreen(purpose) {
    return purpose === "bonus" ? "b" : purpose === "follow_up" ? "f" : "a";
}
/** Message prepared in the care chat, so the team recognises the event by its code word. */
export function acquisitionCareText({ text, purpose }) {
    const phrase = text.trim();
    return purpose === "bonus"
        ? `Здравствуйте! Кодовое слово — «${phrase}». Хочу получить материалы эфира и записаться на бесплатную консультацию.`
        : `Здравствуйте! Кодовое слово — «${phrase}». Хочу записаться на бесплатную консультацию.`;
}
export const AcquisitionPhraseSchema = z.object({
    eventId: z.string().uuid(), key: z.string().regex(/^[0-9a-z]{7}$/u), text: z.string().min(1).max(200), purpose: z.enum(ACQUISITION_PHRASE_PURPOSES),
    validFrom: NativeCalendarDateSchema, validTo: NativeCalendarDateSchema.nullable(), careText: z.string().min(1).max(600),
    /** «Сначала кружочек Дмитрия» (circle) or «Запись сразу» (none), resolved: a word saved before 20.10.2026 takes its event's
     * former switch «Квалификация перед записью». */
    qualification: z.enum(["none", "circle"]).default("none"),
}).strict();
const AcquisitionSeriesEventSchema = z.object({ id: z.string().uuid(), title: z.string(), date: NativeCalendarDateSchema, time: z.string().nullable() }).strict();
/** A broadcast series: its days by date and time (the first day owns the registration settings and the screen links)
 * and its follow-up (дожим) events. An event outside a series is a series of itself. */
export const AcquisitionSeriesSchema = z.object({
    key: z.string().min(1).max(500), title: z.string().min(1).max(200),
    events: z.array(AcquisitionSeriesEventSchema).min(1).max(100),
    followUp: z.array(AcquisitionSeriesEventSchema).max(100).default([]),
}).strict();
export const AcquisitionEventSchema = z.object({
    id: z.string().uuid(), title: z.string(), date: NativeCalendarDateSchema,
    time: z.string().nullable(), campaignKey: z.string(),
    phrases: z.array(AcquisitionPhraseSchema).max(200).default([]),
    series: AcquisitionSeriesSchema.optional(),
}).strict();
export const AcquisitionEventResultSchema = z.object({ event: AcquisitionEventSchema, settings: AcquisitionSettingsSchema }).strict();
// A webinar without registration (Паша 03.10.2026: a simple broadcast for everyone, then a link to the consultation)
// is enabled without a channel: the Mini App consultation and materials screens do not need one, and its
// registration screen without a channel offers the care chat. The Mini App opens the care chat itself with the
// event code word; careLinks stay stored for older pages.
export const AcquisitionSettingsMutationSchema = z.object({
    idempotencyKey: z.string().min(16).max(160), expectedVersion: z.number().int().nonnegative(),
    settings: AcquisitionSettingsSchema.omit({ version: true }),
}).strict();
export const AcquisitionIntentSchema = z.object({
    registrationId: z.string().uuid(), eventId: z.string().uuid(),
    purpose: z.enum(["application", "bonus", "follow_up"]), telegramUserId: z.string().regex(/^[1-9]\d{0,19}$/u),
    crmContactId: z.string().regex(/^[1-9]\d{0,18}$/u).optional(),
    crmLeadId: z.string().regex(/^[1-9]\d{0,18}$/u).optional(),
    occurredAt: z.string().datetime({ offset: true }), isTest: z.boolean().default(false),
}).strict();
/** What the person did in the Mini App on a webinar screen, for Yana (Ден 19.10.2026): the screen (purpose), the code
 * word of the QR and, on the materials screen, when the closed-channel button was pressed. Sent on the screen's
 * registration and again on the press; Vekha keeps one row per registration and the materials link it showed. */
export const AcquisitionEntryFactsSchema = z.object({
    registrationId: z.string().uuid(), eventId: z.string().uuid(),
    purpose: z.enum(["application", "bonus", "follow_up"]), telegramUserId: z.string().regex(/^[1-9]\d{0,19}$/u),
    crmLeadId: z.string().regex(/^[1-9]\d{0,18}$/u).optional(),
    phraseKey: z.string().regex(/^[0-9a-z]{7}$/u).optional(),
    registeredAt: z.string().datetime({ offset: true }),
    materialsOpenedAt: z.string().datetime({ offset: true }).optional(),
    isTest: z.boolean().default(false),
}).strict();
/** The signed links of the Mini App screens of a webinar's code words (GET /api/acquisition/events/:id/previews). */
export const AcquisitionPreviewsSchema = z.object({ expiresAt: z.string().datetime(), previews: z.array(z.object({
        key: z.string().regex(/^[0-9a-z]{7}$/u), purpose: z.enum(ACQUISITION_PHRASE_PURPOSES), eventId: z.string().uuid(), url: z.string().url(),
    }).strict()).max(200) }).strict();
//# sourceMappingURL=acquisition.js.map